/**
 * Thème de la console (lot 3, validé le 21/10/2026) : aux couleurs de Suklu,
 * vert profond et jaune, titres en Manrope, texte en Source Sans 3, clair et
 * sombre. Les écrans prennent leurs couleurs dans le thème (`primary`,
 * `text.secondary`, `divider`…) ; seuls les aperçus « téléphone » gardent
 * les couleurs de l'app.
 */
import { PaletteMode } from '@mui/material';
import { createTheme, Theme } from '@mui/material/styles';

/** Couleurs de la marque, partagées par les deux modes. */
export const COULEURS_SUKLU = {
  vertProfond: '#0b3d22',
  vert: '#14803a',
  vertClair: '#3ccf6b',
  jaune: '#f2b01e',
} as const;

/** Menu latéral : toujours vert profond, quel que soit le mode. */
export const MENU_LATERAL = {
  fond: COULEURS_SUKLU.vertProfond,
  texte: '#d9ecdf',
  texteActif: '#ffffff',
  fondActif: COULEURS_SUKLU.vert,
  survol: 'rgba(255, 255, 255, 0.08)',
} as const;

const POLICE_TITRES = "'Manrope', 'Segoe UI', system-ui, sans-serif";
const POLICE_TEXTE = "'Source Sans 3', 'Segoe UI', system-ui, sans-serif";
const RAYON = 10;

/** Couleurs propres à chaque mode. */
const PALETTES = {
  light: {
    primary: { main: COULEURS_SUKLU.vert, dark: COULEURS_SUKLU.vertProfond, contrastText: '#ffffff' },
    secondary: { main: COULEURS_SUKLU.jaune, contrastText: '#12261a' },
    background: { default: '#f6f8f5', paper: '#ffffff' },
    text: { primary: '#12261a', secondary: '#5d6e62' },
    divider: '#dfe6df',
  },
  dark: {
    primary: { main: COULEURS_SUKLU.vertClair, dark: COULEURS_SUKLU.vert, contrastText: '#0b1a11' },
    secondary: { main: '#f5c04a', contrastText: '#12261a' },
    background: { default: '#0e1712', paper: '#16231b' },
    text: { primary: '#e8f1ea', secondary: '#9db3a3' },
    divider: '#27382d',
  },
} as const;

/** Titres : Manrope, graisse forte, sans majuscules forcées. */
const typographie = {
  fontFamily: POLICE_TEXTE,
  h1: { fontFamily: POLICE_TITRES, fontWeight: 800 },
  h2: { fontFamily: POLICE_TITRES, fontWeight: 800 },
  h3: { fontFamily: POLICE_TITRES, fontWeight: 700 },
  h4: { fontFamily: POLICE_TITRES, fontWeight: 700 },
  h5: { fontFamily: POLICE_TITRES, fontWeight: 700 },
  h6: { fontFamily: POLICE_TITRES, fontWeight: 700 },
  subtitle1: { fontWeight: 600 },
  button: { textTransform: 'none' as const, fontWeight: 600 },
};

/** Réglages communs des composants : onglets soulignés de jaune, cartes sobres. */
const composants = (mode: PaletteMode): Theme['components'] => ({
  MuiAppBar: {
    defaultProps: { elevation: 0, color: 'inherit' },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiTabs: {
    styleOverrides: { indicator: { backgroundColor: COULEURS_SUKLU.jaune, height: 3, borderRadius: 3 } },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        '&.Mui-selected': { color: mode === 'light' ? COULEURS_SUKLU.vertProfond : theme.palette.text.primary },
      }),
    },
  },
  MuiButton: { defaultProps: { disableElevation: true } },
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: RAYON },
      elevation1: ({ theme }) => ({ boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }),
    },
  },
  MuiCard: { defaultProps: { variant: 'outlined' } },
  MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
});

/** Thème complet de la console pour un mode. */
export const creerTheme = (mode: PaletteMode): Theme => createTheme({
  palette: { mode, ...PALETTES[mode] },
  typography: typographie,
  shape: { borderRadius: RAYON },
  components: composants(mode),
});
